import {Injectable} from '@angular/core';
import * as moment from 'moment-timezone';

export interface TimezoneOption {
  value: string;
  display: string;
  abbreviation: string;
  group: string;
}

@Injectable({
  providedIn: 'root',
})
export class TimezoneOptionsService {
  private readonly commonTimezones = [
    'Pacific/Midway',
    'Pacific/Honolulu',
    'America/Anchorage',
    'America/Los_Angeles',
    'America/Vancouver',
    'America/Denver',
    'America/Phoenix',
    'America/Edmonton',
    'America/Chicago',
    'America/Mexico_City',
    'America/Lima',
    'America/Jamaica',
    'America/New_York',
    'America/Toronto',
    'America/Montreal',
    'America/Puerto_Rico',
    'America/Sao_Paulo',
    'America/Buenos_Aires',
    'Atlantic/Reykjavik',
    'Africa/Monrovia',
    'Europe/London',
    'Africa/Lagos',
    'Europe/Brussels',
    'Europe/Copenhagen',
    'Europe/Paris',
    'Europe/Madrid',
    'Europe/Berlin',
    'Europe/Prague',
    'Europe/Warsaw',
    'Europe/Rome',
    'Africa/Johannesburg',
    'Africa/Cairo',
    'Europe/Athens',
    'Asia/Jerusalem',
    'Europe/Moscow',
    'Asia/Tehran',
    'Asia/Kolkata',
    'Australia/Perth',
    'Asia/Shanghai',
    'Asia/Hong_Kong',
    'Asia/Manila',
    'Asia/Seoul',
    'Asia/Tokyo',
    'Pacific/Guam',
    'Australia/Sydney',
    'Pacific/Auckland',
  ];

  private readonly usaTimezones = [
    'Pacific/Honolulu',
    'America/Anchorage',
    'America/Los_Angeles',
    'America/Denver',
    'America/Phoenix',
    'America/Chicago',
    'America/New_York',
    'America/Puerto_Rico',
  ];

  getTimezoneOptions(): TimezoneOption[] {
    const isUserInUSA = this.isUserInUSA();
    let orderedTimezones = [...this.commonTimezones];

    if (isUserInUSA) {
      // Separate USA and non-USA timezones
      const usaTimezones = orderedTimezones.filter(tz => this.usaTimezones.includes(tz));
      const nonUsaTimezones = orderedTimezones.filter(tz => !this.usaTimezones.includes(tz));

      // Put USA timezones first
      orderedTimezones = [...usaTimezones, ...nonUsaTimezones];
    }

    return orderedTimezones.map(timezone => {
      const momentTz = moment.tz(timezone);
      return {
        value: timezone,
        display: this.formatTimezoneDisplay(timezone),
        abbreviation: momentTz.zoneAbbr(),
        group: this.getTimezoneGroup(timezone),
      };
    });
  }

  private isUserInUSA(): boolean {
    const userTimezone = moment.tz.guess();
    return this.usaTimezones.includes(userTimezone);
  }

  getTimezonesByGroup(): Record<string, TimezoneOption[]> {
    const options = this.getTimezoneOptions();
    const grouped: Record<string, TimezoneOption[]> = {};

    options.forEach(option => {
      if (!grouped[option.group]) {
        grouped[option.group] = [];
      }
      grouped[option.group].push(option);
    });

    return grouped;
  }

  getCurrentTimezoneOption(timezone?: string): TimezoneOption | null {
    const currentTimezone = timezone || moment.tz.guess();
    const options = this.getTimezoneOptions();
    return options.find(option => option.value === currentTimezone) || null;
  }

  private formatTimezoneDisplay(timezone: string): string {
    const parts = timezone.split('/');
    const city = parts[parts.length - 1].replace(/_/g, ' ');

    const cityNames: Record<string, string> = {
      'New York': 'Eastern Time',
      Chicago: 'Central Time',
      Denver: 'Mountain Time',
      'Los Angeles': 'Pacific Time',
      Anchorage: 'Alaska Time',
      Honolulu: 'Hawaii Time',
    };

    const displayName = cityNames[city] || city;
    const offset = moment.tz(timezone).format('Z');

    return `${displayName} (${offset})`;
  }

  private getTimezoneGroup(timezone: string): string {
    const region = timezone.split('/')[0];
    const groupNames: Record<string, string> = {
      America: 'Americas',
      Europe: 'Europe',
      Asia: 'Asia',
      Australia: 'Australia',
      Pacific: 'Pacific',
      Africa: 'Africa',
    };
    return groupNames[region] || 'Other';
  }
}
